import { Component, Inject, OnInit, Optional, ViewChild, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { API_BASE_URL, MyAccountClient, PurchaseHistoryDTO, User, PurchaseHistoryDTOPagedResult } from 'src/app/Services/WOWApi.Service';
import { CommonService } from 'src/app/Services/common.service';
import { CustomServiceService } from 'src/app/Services/custom-service.service';
import { VideoPopupComponent } from '../video-popup/video-popup.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

interface WebinarRuntimeLookup {
  webinarGuid: string;
  status: string;
}

@Component({
    selector: 'app-purchase-history-table',
    templateUrl: './purchase-history-table.component.html',
    styleUrls: ['./purchase-history-table.component.css'],
    standalone: false
})
export class PurchaseHistoryTableComponent implements OnInit, OnDestroy {
 
  displayedColumns: string[] = ['productReview', 'seatPurchased', 'totalPrice'];
  dataSource: MatTableDataSource<PurchaseHistoryDTO>;
  purchaseHitory: PurchaseHistoryDTO[] = [];

  loggedUser: User;
  isMobileView: boolean = false;
  subscription: Subscription;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  pageSize = 10;
  pageIndex = 0;
  totalCount = 0;
  pageSizeOptions = [5, 10, 20, 50];
  isLoading = false;

  // webinarId -> current run lookup, refreshed each time a page of purchases
  // loads. Not yet in the generated WOWApi.Service client (added to the API
  // after the last NSwag regeneration), so this calls it directly the same
  // way add-lucky-bucks.component.ts does for its not-yet-generated routes.
  private runtimesByWebinarId: { [webinarId: number]: WebinarRuntimeLookup } = {};
  private readonly baseUrl: string;

  constructor(
    private myAccountClient: MyAccountClient,
    private customServices: CustomServiceService,
    private commonService: CommonService,
    private dialog: MatDialog,
    private http: HttpClient,
    private router: Router,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
    this.subscription = this.commonService.isMobileView.subscribe(value => {
      this.isMobileView = value;
    });
  }

  ngOnInit(): void {
    this.loggedUser = this.customServices.GetLoggedInUser();
    
    if (this.loggedUser && this.loggedUser.id) {
      this.loadPurchaseHistory();
    } else {
      console.error('User not logged in');
      this.isLoading = false;
      this.purchaseHitory = [];
      this.dataSource = new MatTableDataSource([]);
    }
  }

  loadPurchaseHistory(): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.commonService.setSpinner(true);
    
    const pageNumber = this.pageIndex + 1;
    this.purchaseHitory = [];
    
    this.myAccountClient.getPurchaseHistoryPaged(this.loggedUser.id, pageNumber, this.pageSize).subscribe({
      next: (response: PurchaseHistoryDTOPagedResult) => {
        this.purchaseHitory = response.data || [];
        this.totalCount = response.totalCount || 0;
        this.dataSource = new MatTableDataSource(this.purchaseHitory);
        this.isLoading = false;
        this.commonService.setSpinner(false);
        this.loadRuntimeStatuses();
      },
      error: (error) => {
        console.error('Error loading purchase history:', error);
        this.purchaseHitory = [];
        this.totalCount = 0;
        this.dataSource = new MatTableDataSource([]);
        this.isLoading = false;
        this.commonService.setSpinner(false);
      }
    });
  }

  /** One batched call for the whole page instead of one per card. */
  private loadRuntimeStatuses(): void {
    this.runtimesByWebinarId = {};
    const webinarIds = [...new Set(this.purchaseHitory.map(row => row.webinarId))];
    if (webinarIds.length === 0) {
      return;
    }
    this.http.post<{ [webinarId: number]: WebinarRuntimeLookup }>(
      `${this.baseUrl}/api/WebinarRun/GetRuntimesForWebinars`,
      webinarIds
    ).subscribe({
      next: (result) => this.runtimesByWebinarId = result || {},
      error: (error) => console.error('Error loading webinar run statuses:', error)
    });
  }

  isRunActive(row: PurchaseHistoryDTO): boolean {
    const status = this.runtimesByWebinarId[row.webinarId]?.status;
    return !!status && status !== 'Completed' && status !== 'Closed';
  }

  getRunLabel(row: PurchaseHistoryDTO): string {
    return this.runtimesByWebinarId[row.webinarId]?.status === 'Not Started' ? '🟡 Starting soon' : '🔴 Running now';
  }

  openWebinarRun(row: PurchaseHistoryDTO): void {
    const guid = this.runtimesByWebinarId[row.webinarId]?.webinarGuid;
    if (!guid) {
      return;
    }
    this.router.navigate(['/run', guid]);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.loadPurchaseHistory();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  openVideoPopup(videoPath: string): void {
    this.dialog.open(VideoPopupComponent, VideoPopupComponent.openConfig({ videoPath }));
  }

  openWebinarVideoPopup(webinarVideoPath: string): void {
    this.dialog.open(VideoPopupComponent, VideoPopupComponent.openConfig({ videoPath: webinarVideoPath }));
  }

  getRoomName(row: PurchaseHistoryDTO): string {
    return row.roomCurrentName ?? row.roomName ?? '';
  }

  hasVideos(row: PurchaseHistoryDTO): boolean {
    return row.isWebinarCompleted && !!(row.webinarVideopath || row.videorecordingPath);
  }

  getSeatNumbers(row: PurchaseHistoryDTO): string[] {
    if (!row.seatNumbers) {
      return [];
    }
    return row.seatNumbers.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }

  get firstName(): string {
    const name = (this.loggedUser?.firstName || '').trim();
    return name || 'there';
  }
}
